import { Dispatch, Ref, RefObject, SetStateAction } from 'react'
import { IconButton } from 'utoniq-ui'

import {
  ArtistGachaDetailFragment,
  GachasDetailConnectionFragment,
  GachaTicketConnectionFragment,
  Maybe,
  PrepaidPointBalance,
  PurchasableGachaTicket,
  PurchasablePrepaidPointConnectionFragment,
  PurchasablePrepaidPointDetailFragment,
} from '@/clients/utoniq-core/schema'
import { BaseLayout, Head, SideMenu, SideMenuProps } from '@/components/layouts'
import {
  FullScreenAnimationCell,
  VideoFullScreen,
  VideoFullScreenRef,
} from '@/components/lib/animation'
import { LoadingWithBackdrop } from '@/components/organisms'
import { GpsClientInfo } from '@/components/organisms/gps'
import { ArtistThemeProvider } from '@/components/styles'
import { config } from '@/config'
import { Template } from '@/core/architecture'
export * from './fragments'

import { PointChargeDrawer } from '@/components/organisms/live/LiveCommentInput/PointChargeDrawer'
import { useRouter } from '@/core'

import {
  CompleteTicketChargeModal,
  ConfirmationRollGachaByPointModal,
  ConfirmationRollGachaByTicketModal,
  ConfirmTicketChargeModal,
  ErrorTicketChargeModal,
  GachaBody,
  HeaderPurchase,
  InsufficientGachaTicketModal,
  PointInsufficientModal,
  PurchaseGachaTicketDrawer,
} from './components'
import { SkipButton } from './components/SkipButton'
import { StandbyTrigger } from './components/StandbyTrigger'

export * from './fragments'

export type State = {
  artistId: string
  /** ユーザーの認証情報 */
  user: Maybe<SideMenuProps['user']>
  /** ユーザーがアーティストかどうか */
  isArtist: Maybe<SideMenuProps['isArtist']>
  /** 当該アーティストが現在行なっているライブ配信ID(あれば) */
  liveStreamId: string | undefined
  /** サイドメニューのフォロー中アーティスト */
  followArtists: Maybe<SideMenuProps['followArtists']>
  /** サイドメニューのメンバーシップ参加中アーティスト */
  memberArtists: Maybe<SideMenuProps['memberArtists']>
  /** データフェッチ状態 */
  loading: boolean
  /** 当該アーティストのデータ */
  artist: Maybe<ArtistGachaDetailFragment>
  /** アーティストのガチャ一覧データ */
  gachas: Maybe<GachasDetailConnectionFragment>
  /** 今表示しているガチャのID */
  gachaId: string
  /** ガチャチケット購入中のデータフェッチ状態 */
  gachaTicketPurchasing: boolean
  /** ユーザーが所持しているポイント */
  prepaidPointBalance: Maybe<PrepaidPointBalance>
  /** ユーザーが所持しているチケット */
  ticket: number | string
  /** アプリのガチャチケットのデータ */
  gachaTicket: Maybe<GachaTicketConnectionFragment>
  /** ユーザーが購入できるアプリのポイント */
  purchasablePrepaidPoints: Maybe<PurchasablePrepaidPointConnectionFragment>
  /** ポイント購入中のデータフェッチ状態 */
  pointChargeLoading: boolean
  /** ユーザーがクレカを登録しているか */
  isCardRegistered: boolean
  /** headタグのタイトル */
  title: (Maybe<string> | undefined)[]
  /** ガチャを回している最中かどうか */
  gachaRolling: boolean
  /** Apngアニメーションの再生状態 */
  apngAnimationPlaying: boolean
  /** Apngアニメーションを再生するためのHTML要素のRef */
  apngFullScreenRef: Ref<HTMLCanvasElement>
  /** GPSガチャのユーザー位置情報 */
  gpsClientInfo: GpsClientInfo
  /** MP4アニメーションを再生するためのHTML要素のRef */
  videoFullscreenRef: RefObject<VideoFullScreenRef>
  /** スタンバイ動画を再生するためのHTML要素のRef */
  standbyVideoFullscreenRef: RefObject<VideoFullScreenRef>
  /** スタンバイ状態かどうか */
  standingBy: boolean
  /** スタンバイ・本動画含めて動画を再生している時間かどうか */
  playingAnimation: boolean
  /** 回そうとしているガチャが単数か10連かどうかの状態 */
  gachaCount: number
  /** サイドメニューの開閉状態 */
  openSideMenu: boolean
  /** チケット購入アクションシートの開閉状態 */
  openGachaTicketPurchaseDrawer: boolean
  /** ポイント購入アクションシートの開閉状態 */
  openPointPurchaseDrawer: boolean
  /** チケットでガチャを回す際の確認モーダルの表示状態 */
  openConfirmByTicket: boolean
  /** ポイントでガチャを回す際の確認モーダルの表示状態 */
  openConfirmByPoint: boolean
  /** チケット・ポイント両対応のガチャで、ガチャチケ不足時にポイントでガチャを引けるモーダルの表示状態 */
  openInsufficientTicket: boolean
  /** ポイントでガチャを引く際に、ポイントが不足している場合に表示されるモーダルの表示状態 */
  openInsufficientPointModal: boolean
  /** チケット・ポイント両対応のガチャで、ガチャチケ不足時にポイントでガチャを引けるモーダルの表示状態 */
  openTicketPurchaseConfirmModal: boolean
  openTicketPurchaseCompleteModal: boolean
  /** チケット購入後に表示されるモーダルの表示状態 */
  openTicketPurchaseErrorModal: boolean
  /*: ガチャチケット購入時、購入するチケットの枚数の状態 */
  purchasableGachaTicket: Maybe<Omit<PurchasableGachaTicket, 'id'>>
  /* 現在カルーセル表示しているガチャのインデックス番号 */
  initialSlide: number | undefined
  /* 現在カルーセルで表示しているガチャのデータ */
  currentGacha: GachasDetailConnectionFragment['edges'][number]['node'] | null
  /** ガチャ一覧の先頭ガチャのデータ */
  latestGacha: GachasDetailConnectionFragment['edges'][number]['node'] | null
  /** デバイスがSafariエンジンかどうか */
  isSafari: boolean
}

export type Action = {
  /** カルーセルをスライドした際の挙動 */
  handleAfterChange: (currentSlide: number) => void
  /** ガチャチケットを購入する処理 */
  handlePurchaseGachaTicket: () => Promise<void>
  /** ガチャを回すボタンをクリックしたときの処理。条件に応じたモーダルやメッセージを表示する */
  handleClickGachaTrigger: (count: number) => Promise<void>
  /** チケットでガチャを回す処理 */
  handleRollGachaByTicket: () => Promise<void>
  /** ポイントでガチャを回す処理 */
  handleRollGachaByPoint: () => Promise<void>
  /** ポイント購入処理 */
  handlePurchasePoint: (
    plan: PurchasablePrepaidPointDetailFragment
  ) => Promise<void>
  /** ガチャチケット購入のアクションシートをキャンセルするに呼ばれる処理 */
  onCancelPurchaseGachaTicket: () => void
  /** チケット確認モーダルをキャンセルで閉じる際に呼ばれる処理 */
  onCancelPurchaseGachaTicketModal: () => void
  /** チケット購入時にポイント不足によるエラーモーダルを閉じる際に呼ばれる処理 */
  onCancelPurchaseErrorGachaTicket: () => void
  /** サイドメニュー開閉状態の更新処理 */
  setOpenSideMenu: Dispatch<SetStateAction<boolean>>
  /** チケット購入アクションシート表示状態の更新処理 */
  setOpenGachaTicketPurchaseDrawer: Dispatch<SetStateAction<boolean>>
  /** ポイント購入アクションシート表示状態の更新処理 */
  setOpenPointPurchaseDrawer: Dispatch<SetStateAction<boolean>>
  /** チケットでガチャを回すことを確認するモーダルの表示状態の更新処理 */
  setOpenConfirmByTicket: Dispatch<SetStateAction<boolean>>
  /** ポイントでガチャを回すことを確認するモーダルの表示状態の更新処理 */
  setOpenConfirmByPoint: Dispatch<SetStateAction<boolean>>
  /** チケット・ポイント両対応のガチャで、ガチャチケ不足時にポイントでガチャを引けるモーダルの表示状態の更新処理 */
  setOpenInsufficientTicket: Dispatch<SetStateAction<boolean>>
  /** チケット購入時に表示されるモーダルの表示状態の更新処理 */
  setOpenTicketPurchaseConfirmModal: Dispatch<SetStateAction<boolean>>
  /** ポイントガチャを回す時にポイントが不足している場合表示されるモーダルの表示状態の更新処理 */
  setOpenInsufficientPointModal: Dispatch<SetStateAction<boolean>>
  /** チケット購入後に表示されるモーダルの表示状態の更新処理 */
  setOpenTicketPurchaseCompleteModal: Dispatch<SetStateAction<boolean>>
  /** ガチャチケット購入時、購入するチケットの枚数の状態の更新処理 */
  setPurchasableGachaTicket: Dispatch<
    SetStateAction<Maybe<Omit<PurchasableGachaTicket, 'id'>>>
  >
  /** スタンバイ状態から動画をガチャ結果のアニメーションを再生する処理 */
  handleRollGachaAfterStandingby: () => void
  /** 動画スキップ処理 */
  onSkip: () => void
}

/**
 * ガチャ詳細ページ
 * デザイン
 * https://zpl.io/VDgN4LO
 */
export const GachaDetailTemplate: Template<State, Action> = ({
  state,
  handleAfterChange,
  handlePurchaseGachaTicket,
  handlePurchasePoint,
  handleClickGachaTrigger,
  handleRollGachaByTicket,
  handleRollGachaByPoint,
  onCancelPurchaseGachaTicket,
  onCancelPurchaseErrorGachaTicket,
  onCancelPurchaseGachaTicketModal,
  onSkip,
  setOpenSideMenu,
  setOpenGachaTicketPurchaseDrawer,
  setOpenPointPurchaseDrawer,
  setOpenConfirmByTicket,
  setOpenConfirmByPoint,
  setOpenInsufficientTicket,
  setOpenInsufficientPointModal,
  setOpenTicketPurchaseConfirmModal,
  setOpenTicketPurchaseCompleteModal,
  setPurchasableGachaTicket,
  handleRollGachaAfterStandingby,
}) => {
  const { routerPages, generatePath, router } = useRouter()
  // currentGacha が null の場合最新のガチャに遷移する
  if (state.currentGacha === null && !state.loading && state.artistId) {
    router.replace(
      generatePath(
        routerPages[
          state.latestGacha ? 'ArtistGachaDetailLatest' : 'ArtistGachas'
        ],
        {
          artistId: state.artistId,
        }
      )
    )
    return null
  }

  /**
   *  Apngアニメーション再生コンポーネント
   */
  if (state.apngAnimationPlaying)
    return (
      <>
        <FullScreenAnimationCell ref={state.apngFullScreenRef} />
        <SkipButton onSkip={onSkip} />
      </>
    )

  return (
    <ArtistThemeProvider>
      <BaseLayout
        navigation={{
          type: 'artist',
          props: {
            isLive: !!state.liveStreamId,
            hasManagementTask: !!state.user?.hasManagementTask,
            unreadNotificationCount: state.user?.unreadNotificationCount,
          },
        }}
        // fixed issue scroll on PC
        sx={{ height: '100vh' }}
        header={{
          title: state.currentGacha?.name,
          left: config.applicationType === 'organization' && (
            <IconButton.AvatarNoImage
              edge="start"
              onClick={() => setOpenSideMenu(!state.openSideMenu)}
              color="icon.line"
              bgcolor="icon.background"
              badge={
                state.user?.hasManagementTask ||
                !!state.user?.unreadNotificationCount
              }
            />
          ),
          right: state.currentGacha?.isAccessible && (
            /**
             * ヘッダー右上でポイントまたはチケットを購入できる導線
             */
            <HeaderPurchase
              available={!!state.user && !!state.currentGacha}
              type={state.currentGacha?.gachaRollType}
              point={
                state.prepaidPointBalance?.formattedPrepaidPointBarance ?? 0
              }
              onClickPoint={() => setOpenPointPurchaseDrawer(true)}
              ticket={state?.ticket ?? ''}
              onClickGachaTicket={() => setOpenGachaTicketPurchaseDrawer(true)}
              isGachaTicketPurchasable={!!state?.gachaTicket?.isPurchasable}
              gachaTicketThumbnail={
                state.gachaTicket?.thumbnails?.standard?.smallUri || ''
              }
            />
          ),
        }}
      >
        <Head title={state.title} />

        {/**
         * サイドメニュー
         */}
        <SideMenu
          followArtists={state.followArtists}
          memberArtists={state.memberArtists}
          open={state.openSideMenu}
          onClose={() => setOpenSideMenu(false)}
          user={state.user}
          isArtist={state.isArtist}
        />

        {/**
         * ガチャの画像・情報・回すボタン
         */}
        <GachaBody
          available={
            state.loading || !!state.latestGacha || !!state.initialSlide
          }
          artistId={state.artistId}
          artist={state.artist}
          loading={state.loading}
          initialSlide={state.initialSlide}
          currentGacha={state.currentGacha ?? null}
          gachas={state.gachas}
          gachaRolling={state.gachaRolling}
          gachaTicket={state.gachaTicket}
          gachaId={state.currentGacha?.id ?? state.latestGacha?.id ?? ''}
          gpsClientInfo={state.gpsClientInfo}
          user={state.user}
          onAfterChange={handleAfterChange}
          onClickTrigger={handleClickGachaTrigger}
        />

        {/**
         * チケット購入ドロワー
         */}
        <PurchaseGachaTicketDrawer
          ticket={state?.ticket ?? 0}
          onClose={onCancelPurchaseGachaTicket}
          gachaId={state.currentGacha?.id ?? state.latestGacha?.id ?? ''}
          artistId={state.currentGacha?.artistId ?? ''}
          prepaidPointBalance={state.prepaidPointBalance}
          purchasableGachaTickets={
            state.currentGacha?.purchasableGachaTickets ?? []
          }
          gachaTicket={state.gachaTicket}
          open={
            state.initialSlide !== undefined &&
            state.openGachaTicketPurchaseDrawer
          }
          setOpenTicketPurchaseConfirmModal={setOpenTicketPurchaseConfirmModal}
          setOpenPointCharge={() => setOpenPointPurchaseDrawer(true)}
          setPurchasableGachaTicket={setPurchasableGachaTicket}
        />

        {/**
         * ポイントチャージ用ドロワー
         */}
        <PointChargeDrawer
          point={state.prepaidPointBalance?.balance ?? 0}
          onPurchase={handlePurchasePoint}
          open={
            state.initialSlide !== undefined && state.openPointPurchaseDrawer
          }
          purchasablePrepaidPoints={state.purchasablePrepaidPoints}
          onClose={() => {
            setOpenPointPurchaseDrawer(false)
          }}
          chargeLoading={state.pointChargeLoading}
          isCardRegistered={state.isCardRegistered}
          pageName="gachaDetail"
        />

        {/**
         * チケット購入確認モーダル
         */}
        <ConfirmTicketChargeModal
          loading={state.gachaTicketPurchasing}
          open={state.openTicketPurchaseConfirmModal}
          onClose={onCancelPurchaseGachaTicketModal}
          count={state.purchasableGachaTicket?.count ?? 0}
          price={state.purchasableGachaTicket?.price ?? 0}
          onConfirm={handlePurchaseGachaTicket}
        />

        {/**
         * チケット購入完了モーダル
         */}
        <CompleteTicketChargeModal
          open={state.openTicketPurchaseCompleteModal}
          onClose={() => {
            setOpenTicketPurchaseCompleteModal(false)
            setOpenGachaTicketPurchaseDrawer(false)
          }}
        />

        {/**
         * チケット購入エラーモーダル
         * ポイント残高が不足しているときに表示する
         */}
        <ErrorTicketChargeModal
          gachaId={state.currentGacha?.id ?? state.latestGacha?.id ?? ''}
          artistId={state.currentGacha?.artistId ?? ''}
          prepaidPointId={state.prepaidPointBalance?.prepaidPointId ?? ''}
          open={state.openTicketPurchaseErrorModal}
          onClick={() => {
            onCancelPurchaseErrorGachaTicket()
            setOpenPointPurchaseDrawer(true)
          }}
          onClose={onCancelPurchaseErrorGachaTicket}
        />

        {/**
         * ポイント不足時にポイント購入を促すモーダル
         */}
        <PointInsufficientModal
          open={state.openInsufficientPointModal}
          onClose={() => setOpenInsufficientPointModal(false)}
          onConfirm={() => {
            setOpenConfirmByPoint(false)
            setOpenPointPurchaseDrawer(true)
            setOpenInsufficientPointModal(false)
          }}
        />

        {/**
         * チケットでガチャを回す際の確認モーダル
         */}
        <ConfirmationRollGachaByTicketModal
          open={state.openConfirmByTicket}
          onClose={() => setOpenConfirmByTicket(false)}
          gachaRolling={state?.gachaRolling}
          onClick={handleRollGachaByTicket}
          count={state.gachaCount}
        />

        {/**
         * ポイントでガチャを回す際の確認モーダル
         */}
        <ConfirmationRollGachaByPointModal
          open={state.openConfirmByPoint}
          onClose={() => setOpenConfirmByPoint(false)}
          gachaRolling={state?.gachaRolling}
          onClick={handleRollGachaByPoint}
          price={(state.currentGacha?.price ?? 0) * state.gachaCount}
          count={state.gachaCount}
        />

        {/**
         * ガチャチケ・ポイント量対応のガチャで、ガチャチケ不足時にポイントでガチャを引けるモーダル
         */}
        <InsufficientGachaTicketModal
          open={state.openInsufficientTicket}
          onClose={() => setOpenInsufficientTicket(false)}
          point={(state.currentGacha?.price ?? 0) * state.gachaCount}
          prepaidPointBalance={state.prepaidPointBalance?.balance ?? 0}
          onClick={async () => {
            if (
              (state.currentGacha?.price ?? 0) >
              (state.prepaidPointBalance?.balance ?? 0)
            ) {
              setOpenInsufficientTicket(false)
              setOpenConfirmByPoint(true)
              return
            }

            await handleRollGachaByPoint()
          }}
          gachaRolling={state.gachaRolling}
        />

        {/**
         * ローディングインジケータを最前面で表示する
         */}
        <LoadingWithBackdrop open={state.gachaRolling} />
      </BaseLayout>
      {/**
       *  ガチャアニメーション用のVideoFullScreen要素
       */}
      <VideoFullScreen
        ref={state.videoFullscreenRef}
        shouldMute={state.isSafari && !state.currentGacha?.standByAnimations}
        sx={{ zIndex: 11 }}
      />
      {/**
       * スタンバイ動画用のVideoFullScreen要素
       */}
      {!!state.currentGacha?.standByAnimations && (
        <VideoFullScreen
          ref={state.standbyVideoFullscreenRef}
          shouldMute={false}
          sx={{ zIndex: 10 }}
        />
      )}
      {/**
       * アニメーション動画を再生させるためのトリガー
       */}
      {!!state.currentGacha?.standByAnimations && state.standingBy && (
        <StandbyTrigger onPlay={handleRollGachaAfterStandingby} />
      )}
      {/**
       * 動画スキップボタン
       */}
      {state.playingAnimation && <SkipButton onSkip={onSkip} />}
    </ArtistThemeProvider>
  )
}
